import { useState, useEffect, useCallback } from 'react';
import { networkAPI, partnershipAPI } from '../services/api';
import { Card, Button, Badge, SectionHeader, Table, Td, Input, Select, Modal, Empty, Spinner } from '../components/ui';
import toast from 'react-hot-toast';

// Tier 1 — MikroTik/RADIUS in-house hotspot system, platform-side prep.
// Router registration + Router<->Site + Router<->EquipmentAsset linking, plus
// monitoring-ready fields (lastSeen/isOnline/connectedClients/apSummary) —
// these stay at their defaults until a later phase's live RouterOS polling
// (needs real hardware) starts writing to them. No live customer/ISP path is
// touched from this screen — it only manages Router records.

const STATUS_COLOR = { registered: 'text-muted', tunnel_up: 'amber', active: 'green' };
const STATUS_LABEL = { registered: 'Registered', tunnel_up: 'Tunnel Up', active: 'Active' };
const ROUTER_STATUSES = ['registered', 'tunnel_up', 'active'];

const MONITORING_COLOR = { pending: 'text-muted', online: 'green', offline: 'red' };
const MONITORING_LABEL = { pending: 'Pending', online: 'Online', offline: 'Offline' };

function errMsg(err, fallback) {
  return err?.message || fallback || 'Something went wrong';
}

const EMPTY_FORM = { name: '', tunnelIp: '', siteId: '', radiusSecret: '', status: 'registered', equipmentAssetId: '' };

const SETUP_TYPES = ['hotspot', 'pppoe', 'both'];
const SETUP_TYPE_LABEL = { hotspot: 'Hotspot', pppoe: 'PPPoE', both: 'Both (Hotspot + PPPoE)' };
const EMPTY_CONFIG_FORM = { setupType: 'hotspot', hotspotPool: '10.5.50.0/24', wanInterface: 'ether1', hotspotInterface: 'bridge-hotspot' };

export function NetworkAdminTab() {
  const [routers, setRouters] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // router being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({}); // routerId -> bool, show real secret vs masked

  const [configFor, setConfigFor] = useState(null); // router the config-script modal is open for
  const [configForm, setConfigForm] = useState(EMPTY_CONFIG_FORM);
  // 'full' = brand-new router (WireGuard + hotspot/PPPoE + RADIUS + DWIFI
  // redirect). 'addon' = a router that already has its tunnel/hotspot/RADIUS
  // working (e.g. Kalogoyi) and just needs the walled-garden + DWIFI
  // redirect piece added on top.
  const [configVariant, setConfigVariant] = useState('full');
  const [configResult, setConfigResult] = useState(null); // { script, vpsInstructions?, warning?, router? }
  const [generating, setGenerating] = useState(false);

  // Router-control (read-only status, Phase 1 — no reboot/reprovision/
  // disconnect yet, see routerApiService.js). Same one-time-reveal UX as
  // the config-script modal for the enable step's password.
  const [controlFor, setControlFor] = useState(null); // router the control modal is open for
  const [enableResult, setEnableResult] = useState(null); // { script, warning } — shown once
  const [statusResult, setStatusResult] = useState(null); // live status payload
  const [statusError, setStatusError] = useState(null); // last status-check failure message, if any
  const [controlBusy, setControlBusy] = useState(false);

  // Fleet health (operational-visibility Phase 1) — every router that's had
  // its control API enabled, at a glance. Loaded on demand (button), not on
  // every page load — this hits every router's stored state, not live
  // hardware, so it's cheap, but no need to fetch it before an admin asks.
  const [fleetHealth, setFleetHealth] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, e] = await Promise.all([networkAPI.listRouters(), partnershipAPI.listSites(), partnershipAPI.listEquipment()]);
      setRouters(r.routers);
      setSites(s.sites);
      setEquipment(e.equipment || []);
    } catch (err) { toast.error(errMsg(err, 'Failed to load routers')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadFleetHealth = async () => {
    setFleetLoading(true);
    try {
      const res = await networkAPI.getFleetHealth();
      setFleetHealth(res.routers);
    } catch (err) { toast.error(errMsg(err, 'Failed to load fleet health')); }
    finally { setFleetLoading(false); }
  };

  // Equipment options for a given site — this is where the admin picks
  // "which already-logged asset IS this physical router" (or leaves it
  // unlinked if none was separately logged for cost tracking).
  const equipmentForSite = (siteId) => equipment.filter((e) => (e.site?._id || e.site) === siteId);

  const create = async () => {
    if (!form.name.trim() || !form.tunnelIp.trim()) { toast.error('Name and tunnel IP are required'); return; }
    setSaving(true);
    try {
      await networkAPI.registerRouter({
        name: form.name.trim(),
        tunnelIp: form.tunnelIp.trim(),
        siteId: form.siteId || undefined,
        radiusSecret: form.radiusSecret.trim() || undefined,
        status: form.status,
        equipmentAssetId: form.equipmentAssetId || undefined,
      });
      toast.success('Router registered');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to register router')); }
    finally { setSaving(false); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      name: r.name || '',
      tunnelIp: r.tunnelIp || '',
      siteId: r.siteId?._id || '',
      radiusSecret: '',
      status: r.status || 'registered',
      equipmentAssetId: r.equipmentAssetId?._id || '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.tunnelIp.trim()) { toast.error('Name and tunnel IP are required'); return; }
    setSaving(true);
    try {
      await networkAPI.updateRouter(editing._id, {
        name: editForm.name.trim(),
        tunnelIp: editForm.tunnelIp.trim(),
        siteId: editForm.siteId || null,
        ...(editForm.radiusSecret.trim() ? { radiusSecret: editForm.radiusSecret.trim() } : {}),
        status: editForm.status,
        equipmentAssetId: editForm.equipmentAssetId || null,
      });
      toast.success('Router updated');
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to update router')); }
    finally { setSaving(false); }
  };

  const copySecret = async (secret) => {
    try { await navigator.clipboard.writeText(secret); toast.success('Secret copied'); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  const openConfig = (r) => {
    setConfigFor(r);
    setConfigForm({
      setupType: r.setupType || 'hotspot',
      hotspotPool: r.hotspotPool || '10.5.50.0/24',
      wanInterface: r.wanInterface || 'ether1',
      hotspotInterface: r.hotspotInterface || 'bridge-hotspot',
    });
    setConfigVariant('full');
    setConfigResult(null);
  };

  const closeConfig = () => { setConfigFor(null); setConfigResult(null); };

  const generateConfig = async () => {
    setGenerating(true);
    try {
      const res = configVariant === 'addon'
        ? await networkAPI.generatePortalAddOn(configFor._id)
        : configVariant === 'provision'
        ? await networkAPI.generateProvisionToken(configFor._id, configForm)
        : await networkAPI.generateConfig(configFor._id, configForm);
      setConfigResult(res);
      toast.success(
        configVariant === 'addon' ? 'Add-on script generated — copy it now'
        : configVariant === 'provision' ? 'Provisioning token issued — single-use, expires in 1 hour'
        : 'Script generated — copy it now, the private key is shown only here'
      );
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to generate config script')); }
    finally { setGenerating(false); }
  };

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  const openControl = (r) => {
    setControlFor(r);
    setEnableResult(null);
    setStatusResult(null);
  };
  const closeControl = () => { setControlFor(null); setEnableResult(null); setStatusResult(null); setStatusError(null); };

  const enableApi = async () => {
    setControlBusy(true);
    try {
      const res = await networkAPI.enableRouterApi(controlFor._id);
      setEnableResult(res);
      toast.success('API enabled — copy the script now, the password is shown only here');
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed to enable router-control API')); }
    finally { setControlBusy(false); }
  };

  const checkStatus = async () => {
    setControlBusy(true);
    setStatusError(null);
    try {
      const res = await networkAPI.getRouterApiStatus(controlFor._id);
      setStatusResult(res.status);
      toast.success('Live status retrieved');
    } catch (err) {
      setStatusResult(null);
      setStatusError(errMsg(err, 'Router is not reachable'));
      toast.error(errMsg(err, 'Failed to reach router'));
    }
    finally { setControlBusy(false); }
  };

  const downloadScript = () => {
    const blob = new Blob([configResult.script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = configVariant === 'addon' ? 'portal-addon' : 'routeros-setup';
    a.download = `${(configFor?.name || 'router').replace(/[^a-z0-9]+/gi, '-')}-${suffix}.rsc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <SectionHeader
        title="Network — Routers"
        action={<Button size="sm" onClick={() => setShowCreate(true)}>+ Register Router</Button>}
      />
      <p className="text-xs text-text-muted -mt-2 mb-3">
        Tier 1 platform-side prep for the in-house MikroTik/RADIUS hotspot system. Registering a router here just
        stores its details — it does not touch any live customer or ISP path. Monitoring columns show "Pending"
        until a later phase's live RouterOS polling (needs real hardware) starts reporting in.
      </p>

      <div className="mb-4 border border-border rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-text-primary">Fleet Health</span>
          <Button size="sm" variant="secondary" loading={fleetLoading} onClick={loadFleetHealth}>
            {fleetHealth ? 'Refresh' : 'Check Fleet Health'}
          </Button>
        </div>
        {!fleetHealth ? (
          <p className="text-xs text-text-muted">Every router with its control API enabled, at a glance — pulls last-known state, doesn't contact any router live.</p>
        ) : fleetHealth.length === 0 ? (
          <p className="text-xs text-text-muted">No router has had its control API enabled yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fleetHealth.map((r) => (
              <div key={r.routerId} className="flex items-center justify-between text-xs bg-bg-panel border border-border rounded px-2 py-1.5">
                <span className="font-semibold text-text-primary truncate">{r.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {r.staleness === 'never' ? (
                    <Badge label="Never checked" color="text-muted" />
                  ) : r.staleness === 'stale' ? (
                    <Badge label="Stale" color="red" />
                  ) : (
                    <Badge label={r.isOnline ? 'Online' : 'Offline'} color={r.isOnline ? 'green' : 'red'} />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <Spinner /> : routers.length === 0 ? (
        <Empty title="No routers registered yet" description="Register the pilot router's details once its WireGuard tunnel is up." />
      ) : (
        <Table headers={['Name', 'Site', 'Tunnel IP', 'Linked Asset', 'Status', 'Monitoring', 'Clients', 'Child Devices', 'RADIUS Secret', '']}>
          {routers.map((r) => (
            <tr key={r._id}>
              <Td className="font-semibold text-text-primary">{r.name}</Td>
              <Td>{r.siteId?.name || <span className="text-text-muted">Unassigned</span>}</Td>
              <Td className="font-mono text-xs">{r.tunnelIp || '—'}</Td>
              <Td className="text-xs">
                {r.equipmentAssetId ? (
                  <span>{r.equipmentAssetId.type}{r.equipmentAssetId.serial ? ` (${r.equipmentAssetId.serial})` : ''}</span>
                ) : <span className="text-text-muted">Not linked</span>}
              </Td>
              <Td><Badge label={STATUS_LABEL[r.status] || r.status} color={STATUS_COLOR[r.status] || 'text-muted'} /></Td>
              <Td><Badge label={MONITORING_LABEL[r.monitoringStatus] || 'Pending'} color={MONITORING_COLOR[r.monitoringStatus] || 'text-muted'} /></Td>
              <Td className="text-xs">{r.connectedClients ?? 0}</Td>
              <Td className="text-xs">{r.apSummary ? `${r.apSummary.activeCount} / ${r.apSummary.count} active` : '0 / 0'}</Td>
              <Td className="font-mono text-xs">
                {r.radiusSecret ? (
                  <span className="inline-flex items-center gap-2">
                    {revealed[r._id] ? r.radiusSecret : '••••••••••••'}
                    <button className="text-cyan hover:underline" onClick={() => setRevealed({ ...revealed, [r._id]: !revealed[r._id] })}>
                      {revealed[r._id] ? 'hide' : 'reveal'}
                    </button>
                    <button className="text-cyan hover:underline" onClick={() => copySecret(r.radiusSecret)}>copy</button>
                  </span>
                ) : '—'}
              </Td>
              <Td>
                <div className="flex flex-col gap-1">
                  <button className="text-xs text-cyan hover:underline" onClick={() => openEdit(r)}>Edit</button>
                  <button className="text-xs text-cyan hover:underline" onClick={() => openConfig(r)}>Generate Config</button>
                  <button className="text-xs text-cyan hover:underline" onClick={() => openControl(r)}>Router Control</button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Register Router">
        <div className="space-y-3">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Namataba RB4011" />
          <Input label="WireGuard Tunnel IP" value={form.tunnelIp} onChange={(e) => setForm({ ...form, tunnelIp: e.target.value })} placeholder="e.g. 10.10.0.2" />
          <Select label="Site" value={form.siteId} onChange={(e) => setForm({ ...form, siteId: e.target.value, equipmentAssetId: '' })}>
            <option value="">Unassigned</option>
            {sites.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
          <Select label="Linked Equipment Asset (optional)" value={form.equipmentAssetId} onChange={(e) => setForm({ ...form, equipmentAssetId: e.target.value })} disabled={!form.siteId}>
            <option value="">Not linked</option>
            {equipmentForSite(form.siteId).map((a) => (
              <option key={a._id} value={a._id}>{a.type}{a.serial ? ` — ${a.serial}` : ''}</option>
            ))}
          </Select>
          <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {ROUTER_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </Select>
          <Input label="RADIUS Client Secret" value={form.radiusSecret} onChange={(e) => setForm({ ...form, radiusSecret: e.target.value })} placeholder="Leave blank to auto-generate" />
          <Button className="w-full" loading={saving} onClick={create}>Register Router</Button>
        </div>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit — ${editing?.name || ''}`}>
        <div className="space-y-3">
          <Input label="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          <Input label="WireGuard Tunnel IP" value={editForm.tunnelIp} onChange={(e) => setEditForm({ ...editForm, tunnelIp: e.target.value })} />
          <Select label="Site" value={editForm.siteId} onChange={(e) => setEditForm({ ...editForm, siteId: e.target.value, equipmentAssetId: '' })}>
            <option value="">Unassigned</option>
            {sites.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
          <Select label="Linked Equipment Asset (optional)" value={editForm.equipmentAssetId} onChange={(e) => setEditForm({ ...editForm, equipmentAssetId: e.target.value })} disabled={!editForm.siteId}>
            <option value="">Not linked</option>
            {equipmentForSite(editForm.siteId).map((a) => (
              <option key={a._id} value={a._id}>{a.type}{a.serial ? ` — ${a.serial}` : ''}</option>
            ))}
          </Select>
          <Select label="Status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
            {ROUTER_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </Select>
          <Input label="RADIUS Client Secret" value={editForm.radiusSecret} onChange={(e) => setEditForm({ ...editForm, radiusSecret: e.target.value })} placeholder="Leave blank to keep the current secret" />
          <Button className="w-full" loading={saving} onClick={saveEdit}>Save Changes</Button>
        </div>
      </Modal>

      <Modal open={!!configFor} onClose={closeConfig} title={`Generate Config Script — ${configFor?.name || ''}`} size="xl">
        {!configResult ? (
          <div className="space-y-3">
            {/* Full Setup vs Portal Add-On vs Self-Provisioning Token — pick the right one before generating */}
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => setConfigVariant('full')}
                className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${configVariant === 'full' ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-muted hover:text-text-primary'}`}
              >
                Full Setup <span className="block font-normal opacity-80">Brand-new router</span>
              </button>
              <button
                type="button"
                onClick={() => setConfigVariant('addon')}
                className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors border-l border-border ${configVariant === 'addon' ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-muted hover:text-text-primary'}`}
              >
                Portal Add-On <span className="block font-normal opacity-80">Router already working</span>
              </button>
              <button
                type="button"
                onClick={() => setConfigVariant('provision')}
                className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors border-l border-border ${configVariant === 'provision' ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-muted hover:text-text-primary'}`}
              >
                Self-Provision <span className="block font-normal opacity-80">One-line, brand-new router</span>
              </button>
            </div>

            {configVariant === 'full' || configVariant === 'provision' ? (
              <>
                <p className="text-xs text-text-muted">
                  {configVariant === 'provision'
                    ? 'Issues a single-use, 1-hour token instead of a script — the router fetches and applies its own config with one pasted command. The WireGuard private key is staged encrypted until fetched, then cleared immediately.'
                    : 'Generates a complete RouterOS v7 script to paste into WinBox. Text only — this never connects to the router. The WireGuard tunnel IP is auto-assigned (next free 10.10.0.x) and shown once the script is generated.'}
                </p>
                <Select label="Setup Type" value={configForm.setupType} onChange={(e) => setConfigForm({ ...configForm, setupType: e.target.value })}>
                  {SETUP_TYPES.map((t) => <option key={t} value={t}>{SETUP_TYPE_LABEL[t]}</option>)}
                </Select>
                {(configForm.setupType === 'hotspot' || configForm.setupType === 'both') && (
                  <Input label="Hotspot IP Pool (CIDR)" value={configForm.hotspotPool} onChange={(e) => setConfigForm({ ...configForm, hotspotPool: e.target.value })} placeholder="10.5.50.0/24" />
                )}
                <Input label="Uplink/WAN Interface" value={configForm.wanInterface} onChange={(e) => setConfigForm({ ...configForm, wanInterface: e.target.value })} placeholder="ether1" />
                <Input label="Hotspot/Customer-facing Interface (bridge name)" value={configForm.hotspotInterface} onChange={(e) => setConfigForm({ ...configForm, hotspotInterface: e.target.value })} placeholder="bridge-hotspot" />
              </>
            ) : (
              <p className="text-xs text-text-muted">
                For a router that already has its WireGuard tunnel, hotspot, and RADIUS client configured and
                working (like Kalogoyi) — adds ONLY the walled-garden entries and the DWIFI captive-portal redirect
                on top. Nothing else on the router is touched, and it's safe to paste more than once.
              </p>
            )}

            <Button className="w-full" loading={generating} onClick={generateConfig}>
              {configVariant === 'addon' ? 'Generate Add-On Script' : configVariant === 'provision' ? 'Issue Provisioning Token' : 'Generate Full Script'}
            </Button>
          </div>
        ) : configVariant === 'provision' ? (
          <div className="space-y-3">
            <Badge label="Self-Provisioning Token" color="amber" />
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              Single-use — expires {new Date(configResult.expiresAt).toLocaleString()}. If the paste fails partway
              (router loses power, network hiccup), come back here and issue a fresh one; the old one stops working
              the moment a new one is issued.
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-text-primary">One-Line Command</span>
                <button className="text-xs font-semibold text-cyan hover:underline" onClick={() => copyText(configResult.oneLinerCommand, 'Command')}>Copy Command</button>
              </div>
              <pre className="text-xs font-mono bg-bg-panel border border-border rounded-lg p-3 overflow-auto whitespace-pre-wrap break-all">{configResult.oneLinerCommand}</pre>
            </div>
            <p className="text-xs text-text-muted">
              Paste into WinBox → New Terminal → Enter on a factory-default router with WAN internet access already
              up. It downloads and applies its complete config (WireGuard tunnel, RADIUS, hotspot, DWIFI portal
              redirect, router-control API) in one step — nothing else to paste.
            </p>
            <Button className="w-full" onClick={closeConfig}>Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Badge label={configVariant === 'addon' ? 'Portal Add-On Script' : 'Full Setup Script'} color={configVariant === 'addon' ? 'cyan' : 'green'} />

            {configResult.warning && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                {configResult.warning}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-text-primary">RouterOS Script</span>
                <div className="flex gap-2">
                  <button className="text-xs font-semibold text-cyan hover:underline" onClick={() => copyText(configResult.script, 'Script')}>Copy Script</button>
                  <button className="text-xs text-cyan hover:underline" onClick={downloadScript}>Download .rsc</button>
                </div>
              </div>
              <pre className="text-xs font-mono bg-bg-panel border border-border rounded-lg p-3 overflow-auto max-h-[28rem] whitespace-pre-wrap">{configResult.script}</pre>
            </div>
            {configResult.vpsInstructions && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-text-primary">VPS-side Checklist (not applied automatically)</span>
                  <button className="text-xs font-semibold text-cyan hover:underline" onClick={() => copyText(configResult.vpsInstructions, 'VPS checklist')}>Copy VPS Checklist</button>
                </div>
                <pre className="text-xs font-mono bg-bg-panel border border-border rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap">{configResult.vpsInstructions}</pre>
              </div>
            )}
            <p className="text-xs text-text-muted">
              {configVariant === 'addon'
                ? 'How to apply: WinBox → New Terminal → paste the script above → Enter. Nothing else on this router is touched.'
                : 'How to apply: WinBox → New Terminal → paste the script above → Enter. Apply the VPS-side checklist separately (or ask for it to be applied) so the tunnel and RADIUS client match.'}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setConfigResult(null)}>Regenerate…</Button>
              <Button className="flex-1" onClick={closeConfig}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!controlFor} onClose={closeControl} title={`Router Control — ${controlFor?.name || ''}`}>
        <div className="space-y-3">
          <p className="text-xs text-text-muted">
            Live connection to this router over the WireGuard tunnel. Read-only for now — status checks only. Reboot
            and remote re-provisioning are designed but not yet available here.
          </p>

          {enableResult ? (
            <>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                {enableResult.warning}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-text-primary">API-Enable Script</span>
                  <button className="text-xs font-semibold text-cyan hover:underline" onClick={() => copyText(enableResult.script, 'Script')}>Copy Script</button>
                </div>
                <pre className="text-xs font-mono bg-bg-panel border border-border rounded-lg p-3 overflow-auto max-h-[24rem] whitespace-pre-wrap">{enableResult.script}</pre>
              </div>
              <p className="text-xs text-text-muted">Paste into WinBox → New Terminal → Enter, then close this and reopen Router Control to check live status.</p>
            </>
          ) : controlFor?.routerApi?.enabledAt ? (
            <>
              <p className="text-xs text-text-muted">
                API enabled {new Date(controlFor.routerApi.enabledAt).toLocaleString()} as user "{controlFor.routerApi.username}".
              </p>
              <Button className="w-full" loading={controlBusy} onClick={checkStatus}>Check Live Status</Button>

              {statusResult && <Badge label="Connected" color="green" />}
              {statusError && (
                <div className="flex items-center gap-2">
                  <Badge label="Not Reachable" color="red" />
                  <span className="text-xs text-text-muted">{statusError}</span>
                </div>
              )}

              {statusResult && (
                <>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">Uptime</span>{statusResult.uptime || '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">CPU Load</span>{statusResult.cpuLoadPercent != null ? `${statusResult.cpuLoadPercent}%` : '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">Free Memory</span>{statusResult.freeMemoryBytes != null ? `${Math.round(statusResult.freeMemoryBytes / 1024 / 1024)} MB` : '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">Active Hotspot Users</span>{statusResult.activeHotspotUsers ?? '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">RouterOS Version</span>{statusResult.routerOsVersion || '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2"><span className="text-text-muted block">WireGuard Last Handshake</span>{statusResult.wireguardLastHandshake || '—'}</div>
                    <div className="bg-bg-panel border border-border rounded-lg p-2 col-span-2"><span className="text-text-muted block">Checked At</span>{statusResult.checkedAt ? new Date(statusResult.checkedAt).toLocaleString() : '—'}</div>
                  </div>

                  {statusResult.interfaces?.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-text-primary block mb-1">Interfaces</span>
                      <div className="flex flex-wrap gap-1">
                        {statusResult.interfaces.map((i) => (
                          <span key={i.name} className={`text-xs font-mono px-2 py-0.5 rounded border ${i.running ? 'border-green-500/40 text-green-600 dark:text-green-400' : 'border-border text-text-muted'}`}>
                            {i.name} {i.disabled ? '(disabled)' : i.running ? '(running)' : '(down)'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {statusResult.activeHotspotUsersList?.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-text-primary block mb-1">Active Hotspot Sessions</span>
                      <div className="space-y-1">
                        {statusResult.activeHotspotUsersList.map((u, idx) => (
                          <div key={idx} className="text-xs font-mono bg-bg-panel border border-border rounded px-2 py-1 flex justify-between">
                            <span>{u.user || u.macAddress || '—'} ({u.address || '—'})</span>
                            <span className="text-text-muted">{u.uptime || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <p className="text-xs text-text-muted">
                This router hasn't had its control API enabled yet. Generates a one-time script (dedicated
                low-privilege RouterOS user, REST API restricted to the VPS's tunnel IP only) — paste it once, then
                come back here to check live status.
              </p>
              <Button className="w-full" loading={controlBusy} onClick={enableApi}>Enable API</Button>
            </>
          )}
        </div>
      </Modal>
    </Card>
  );
}
